import { useUserContext } from "@/context/UserContext";
import { formatMoney } from "@/lib/locale";
import { cn } from "@/lib/utils";
import { CheckIcon, TicketPercentIcon } from "lucide-react";
import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import Button from "./Button";

export default function SubscriptionDetailNew({
  className = "",
  item,
  group,
  recommended,
  currency,
  section,
  benefits,
  noAction,
  onChoose,
  upgradeable,
  isUpgrade,
}: {
  className?: string;
  item?: any;
  group?: string;
  recommended?: boolean;
  currency?: string;
  section?: string;
  benefits?: string[];
  noAction?: boolean;
  onChoose: (item: any) => void;
  upgradeable?: boolean;
  isUpgrade?: boolean;
}) {
  const { status, authModal } = useUserContext();
  const { t } = useTranslation();

  const names = {
    free: t("free"),
    lite: "Lite",
    starter: "Starter",
    pro: "Pro",
    premium: "Premium",
    business: t("business"),
    ultimate: "Ultimate",
  };

  const { price, actual_price, before_price, section_label } = useMemo(() => {
    const res = {
      price: 0,
      actual_price: 0,
      before_price: 0,
      section_label: "mo",
    };
    if (item) {
      if (section === "annually") {
        res.actual_price = formatMoney(item.prices[currency], currency);
        const discPrice = Math.round(item.prices[currency] / 12);
        res.price = formatMoney(discPrice, currency);
        res.before_price = item.monthly
          ? formatMoney(item.monthly.prices[currency], currency)
          : 0;

        if (group === "lite") {
          res.section_label = "yr";
          res.price = formatMoney(item.prices[currency], currency);
          res.before_price = 0;
          res.actual_price = 0;
        }

        return res;
      }

      res.price = formatMoney(item.prices[currency], currency);
      return res;
    }
    return res;
  }, [item, group, currency, section, t]);

  return (
    <div className={cn(className, "w-full p-2")}>
      <div
        className={cn(
          "flex flex-col bg-background p-5 border rounded-xl w-full h-full overflow-hidden",
          recommended ? "border-slate-500 ring-8 ring-slate-500/20" : ""
        )}
      >
        <div className="relative -mx-5 -mt-10 mb-5">
          <div
            className="min-h-[18rem] lg:min-h-[15rem]"
            style={{
              backgroundPosition: "center",
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              backgroundImage: "url(/images/plans/plans-" + group + ".svg)",
            }}
          />
          {item?.is_discount && (
            <div className="right-0 bottom-5 absolute flex justify-center items-center bg-[#10b35f] px-3 py-2 font-montserrat font-bold text-[#f9fbfa]">
              <TicketPercentIcon className="mr-1.5 size-5 -rotate-45" />
              Discount {item?.discount}%
            </div>
          )}
        </div>
        <h3 className="font-worksans font-bold text-2xl">
          {names[group]}
          {recommended && (
            <span className="inline-block bg-primary/90 ml-1 px-1 rounded font-semibold text-white text-xs text-center -translate-y-1">
              {t("recommended")}
            </span>
          )}
        </h3>

        <p className="mt-1 font-worksans font-bold text-foreground/80 text-2xl leading-4">
          {item && group !== "free" ? (
            <>
              {price}
              <span className="text-base">/{t("shorts." + section_label)}</span>
            </>
          ) : (
            <>
              <span className="text-foreground/70">
                {group === "free" ? formatMoney(0, currency) : "-"}
              </span>
              <span className="opacity-0 text-base">
                /{t("shorts." + section_label)}
              </span>
            </>
          )}
        </p>

        {group === "free" && status === "unauthorized" ? (
          <p className="font-worksans text-muted-foreground text-sm">
            <Trans i18nKey="absolutely_free">Absolutely Free</Trans>
          </p>
        ) : (
          <></>
        )}
        <p
          className={cn(
            "font-worksans font-semibold text-foreground/50 text-base line-through leading-4",
            before_price === 0 && "hidden"
          )}
        >
          {before_price}
        </p>
        {section === "annually" && (
          <p
            className={cn(
              "font-worksans text-muted-foreground text-sm",
              actual_price === 0 && "opacity-0"
            )}
          >
            {t("billed_annually")}: {actual_price}
          </p>
        )}

        <ul className="flex flex-col flex-1 gap-1.5 mt-3 font-worksans text-foreground/60">
          {benefits?.map((label, key) => (
            <li key={key} className="flex">
              <div className="shrink-0">
                <CheckIcon className="mt-1 size-4" />
              </div>
              <div className="pl-2">{label}</div>
            </li>
          ))}
        </ul>

        {!noAction && (
          <div className="mt-4">
            {group === "free" ? (
              <>
                {status === "authenticated" ? (
                  <Button
                    variant="ghost"
                    roundFull
                    disabled
                    className="w-full font-bold"
                  >
                    <Trans i18nKey="absolutely_free">Absolutely Free</Trans>
                  </Button>
                ) : (
                  <Button
                    variant="default"
                    roundFull
                    className="bg-blue-500/5 w-full font-bold text-blue-500"
                    onClick={() => authModal.open()}
                  >
                    <Trans i18nKey="register_now">Register Now</Trans>!
                  </Button>
                )}
              </>
            ) : item ? (
              isUpgrade ? (
                <Button
                  variant="outline"
                  roundFull
                  className={cn(
                    "w-full font-bold",
                    upgradeable
                      ? "bg-primary/5 hover:bg-primary/10 border-primary/5 w-full font-bold text-primary hover:text-primary"
                      : ""
                  )}
                  disabled={!upgradeable}
                  onClick={() => onChoose(item)}
                >
                  {upgradeable ? t("upgrade") : t("not_available_upgrade")}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  roundFull
                  className="bg-primary/5 hover:bg-primary/10 border-primary/5 w-full font-bold text-primary hover:text-primary"
                  onClick={() => onChoose(item)}
                >
                  Subscribe
                </Button>
              )
            ) : (
              <Button
                variant="default"
                roundFull
                disabled
                className="w-full font-bold"
              >
                <Trans i18nKey="not_available_monthly_plan">
                  Not Available on Monthly plan
                </Trans>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
